import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { createDgiPortalUser } from "@/services/authService";
import {
  continueOnboardingCaeDownload,
  continueOnboardingCompanyInfo,
  continueOnboardingIdemisorSync,
  continueOnboardingProveedorSoftware,
  getDgiPortalUserAutomationSession,
  getOnboardingAutoSession,
  reportOnboardingFailure,
  startOnboardingAutoFlow,
  submitDgiPortalUserAutomationStep,
} from "@/services/fiscalService";

export type AutoStep =
  | "portal_user"
  | "company_info"
  | "cae_download"
  | "idemisor_sync"
  | "proveedor_software";

export type AutoStepStatus =
  | "pending"
  | "running"
  | "success"
  | "failed"
  | "skipped";

/**
 * Inline prompt the step is waiting on (renders a confirm/cancel CTA card
 * underneath the step row). Currently only used by portal_user during the
 * Borrar Usuario flow when an existing e-Factura user is detected.
 */
export type AutoStepAwaiting = "delete_existing_user";

export interface AutoStepState {
  step: AutoStep;
  label: string;
  description: string;
  status: AutoStepStatus;
  error?: string;
  awaiting?: AutoStepAwaiting | null;
}

export interface ActivityTickerEntry {
  ts: string;
  label: string;
  live?: boolean;
}

export interface UseOnboardingAutoFlowOptions {
  portalUserAlreadyDone?: boolean;
  /** When the chain reaches all-success, the hook fires this so the page can advance the wizard. */
  onAllComplete?: () => void | Promise<void>;
  /** When any step fails terminally, the hook fires this. */
  onFailed?: () => void;
  /**
   * Per-step completion timestamps read from the tenant record. When a step
   * has a non-null completedAt, the hook initializes it as "success" so a
   * refresh resumes at the first incomplete step instead of replaying the
   * whole chain.
   */
  completedSteps?: Partial<Record<AutoStep, string | null>>;
  /**
   * Skip the proveedor_software step entirely. Used for situation "scratch"
   * (the tenant has never issued e-Factura) where Fiscalo is not yet a DGI-
   * homologated software provider for this RUT — registering Fiscalo as the
   * proveedor requires the slow DGI homologation process the user has to do
   * themselves. The rest of the chain (portal_user → company_info → cae_
   * download) still runs end-to-end; proveedor_software stays "skipped" and
   * the flow completes after cae_download.
   */
  skipProveedorSoftware?: boolean;
}

export interface UseOnboardingAutoFlowReturn {
  steps: AutoStepState[];
  activityTicker: ActivityTickerEntry[];
  phase: "idle" | "working" | "done";
  doneCount: number;
  totalCount: number;
  tiempoFmt: string;
  hasFailed: boolean;
  waitingForManualContinue: AutoStep | null;
  portalUserModalOpen: boolean;
  setPortalUserModalOpen: (open: boolean) => void;
  /** True when a sub-step failed due to an expired SSO; consumer should render the re-auth modal. */
  ssoRefreshModalOpen: boolean;
  setSsoRefreshModalOpen: (open: boolean) => void;
  /** Begin the orchestration. */
  start: () => Promise<void>;
  retry: (step: AutoStep) => Promise<void>;
  /** Advance one step manually when DEBUG_STEP_BY_STEP is true. */
  manualContinue: () => Promise<void>;
  /**
   * Fired by the embedded DgiConsultaFlowModal once SSO is complete (the modal
   * closes early after auth — the rest of portal_user runs inline). The hook
   * takes over polling the same session for form-fill progress + final status.
   */
  handlePortalUserSuccess: (sessionId?: string) => Promise<void>;
  handlePortalUserFailed: () => Promise<void>;
  /**
   * Idempotent close-handler: call from the modal's onOpenChange. Treats the
   * close as a failure only when handlePortalUserSuccess hasn't already fired.
   */
  handlePortalUserModalClosed: () => Promise<void>;
  /**
   * User input for portal_user step's inline prompts (e.g. confirm delete
   * existing e-Factura user). Returns a no-op when no prompt is pending.
   */
  confirmDeleteExistingUser: () => Promise<void>;
  declineDeleteExistingUser: () => Promise<void>;
  /** Wire to the embedded DgiConsultaFlowModal (sso-refresh variant). */
  handleSsoRefreshSuccess: () => Promise<void>;
  handleSsoRefreshFailed: () => Promise<void>;
}

/**
 * Substring patterns in the raw Python error string that signal an expired SSO
 * session. When one of these surfaces from a sub-step, the chain pauses, the UI
 * pops the re-auth modal (DgiConsultaFlowModal variant="sso-refresh"), and on
 * successful re-auth the failed step retries with the refreshed storage state.
 */
const SESSION_EXPIRED_PATTERNS: readonly string[] = [
  "consulta datos page still shows login required",
  "cae page still shows login required",
  "e-factura solicitud page still shows login required",
  "session lost",
  "could not reload solicitud",
];

function looksLikeSessionExpired(error: string | undefined | null): boolean {
  if (!error) return false;
  const lower = error.toLowerCase();
  return SESSION_EXPIRED_PATTERNS.some((p) => lower.includes(p));
}

/**
 * Encapsulates the DGI auto-onboarding orchestration: portal user creation,
 * SSO authentication, company info extraction, CAE download, last numbers
 * lookup. Polls the backend every 3s per sub-session.
 *
 * The hook is presentation-agnostic — consumers render the plan/ticker
 * inline (DgiAccessStep) or used to render a modal frame (legacy).
 */
export function useOnboardingAutoFlow({
  portalUserAlreadyDone = false,
  onAllComplete,
  onFailed,
  completedSteps,
  skipProveedorSoftware = false,
}: UseOnboardingAutoFlowOptions = {}): UseOnboardingAutoFlowReturn {
  const { t } = useTranslation();

  // Set to true during development to pause between substeps and require the
  // tester to click "Continuar a {next_step}". Always false in production so
  // the flow auto-chains portal_user → company_info → cae_download.
  const DEBUG_STEP_BY_STEP = false;

  const [orchestrationSessionId, setOrchestrationSessionId] = useState<string | null>(null);
  const [portalUserModalOpen, setPortalUserModalOpen] = useState(false);
  const [ssoRefreshModalOpen, setSsoRefreshModalOpen] = useState(false);
  /** Which step we paused mid-chain to re-authenticate; retried after SSO refresh succeeds. */
  const stepWaitingForReauthRef = useRef<AutoStep | null>(null);
  /** Portal-user session id (captured when modal closes after SSO) — used to poll the form-fill phase inline. */
  const portalUserSessionIdRef = useRef<string | null>(null);
  const portalUserPollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  /**
   * True once the user has clicked Regenerar/Cancelar on the delete-existing-user
   * prompt. Suppresses the polling loop from re-rendering the prompt while the
   * Python flow is still propagating its status away from
   * `awaiting_delete_user_confirmation` (otherwise the prompt flashes back in).
   */
  const deleteConfirmationSentRef = useRef(false);
  const [started, setStarted] = useState(false);
  // Ref-based mirror of `started` for the start() guard. setState batching
  // means two synchronous start() calls both see started===false in their
  // captured closures and both proceed — spawning duplicate Python flows
  // (Playwright windows). The ref flips synchronously so the second call
  // early-returns immediately.
  const startedRef = useRef(false);
  const [activityTicker, setActivityTicker] = useState<ActivityTickerEntry[]>([]);
  const [completedAt, setCompletedAt] = useState<number | null>(null);
  const startedAtRef = useRef<number | null>(null);
  const [waitingForManualContinue, setWaitingForManualContinue] = useState<AutoStep | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const portalUserSucceededRef = useRef(false);
  const lastCompanyInfoDataRef = useRef<Record<string, unknown> | null>(null);
  const completionFiredRef = useRef(false);
  const failureFiredRef = useRef(false);

  // Backend-persisted completion timestamps. When a step has completedAt on
  // the tenant record, its initial status is "success" so a refresh resumes
  // at the first incomplete step instead of replaying expensive DGI work.
  const initialStatus = (step: AutoStep, fallback: AutoStepStatus): AutoStepStatus =>
    completedSteps?.[step] ? "success" : fallback;

  const [steps, setSteps] = useState<AutoStepState[]>(() => [
    {
      step: "portal_user",
      label: t("onboarding.autoFlow.stepPortalUser"),
      description: t(
        "onboarding.autoFlow.stepPortalUserDesc",
        "Creando el usuario técnico para facturación electrónica en DGI.",
      ),
      status: initialStatus(
        "portal_user",
        portalUserAlreadyDone ? "skipped" : "pending",
      ),
    },
    {
      step: "company_info",
      label: t("onboarding.autoFlow.stepCompanyInfo"),
      description: t(
        "onboarding.autoFlow.stepCompanyInfoDesc",
        "Descargando los datos fiscales de tu empresa.",
      ),
      status: initialStatus("company_info", "pending"),
    },
    {
      step: "cae_download",
      label: t("onboarding.autoFlow.stepCaeDownload"),
      description: t(
        "onboarding.autoFlow.stepCaeDownloadDesc",
        "Descargando tus certificados autorizados (CAE).",
      ),
      status: initialStatus("cae_download", "pending"),
    },
    {
      step: "idemisor_sync",
      label: t(
        "onboarding.autoFlow.stepIdemisorSync",
        "Sincronizar IdEmisor con DGI",
      ),
      description: t(
        "onboarding.autoFlow.stepIdemisorSyncDesc",
        "Leemos el último IdEmisor enviado a DGI para continuar la secuencia sin duplicados.",
      ),
      status: initialStatus("idemisor_sync", "pending"),
    },
    {
      step: "proveedor_software",
      label: t(
        "onboarding.autoFlow.stepProveedorSoftware",
        "Configurar Fiscalo como tu proveedor de e-Factura",
      ),
      description: skipProveedorSoftware
        ? t(
            "onboarding.autoFlow.stepProveedorSoftwareSkippedDesc",
            "Vas a registrar a Fiscalo manualmente en DGI cuando estés habilitado como emisor electrónico.",
          )
        : t(
            "onboarding.autoFlow.stepProveedorSoftwareDesc",
            "Configurando el email de contacto y dejando a Fiscalo registrado como tu proveedor.",
          ),
      status: skipProveedorSoftware
        ? "skipped"
        : initialStatus("proveedor_software", "pending"),
    },
  ]);

  const updateStep = useCallback(
    (step: AutoStep, patch: Partial<AutoStepState>) => {
      setSteps((prev) => prev.map((s) => (s.step === step ? { ...s, ...patch } : s)));
    },
    [],
  );

  // Synchronously readable mirror of `steps` — useful inside async callbacks
  // that need to inspect the latest step state without waiting for a render.
  const stepsRef = useRef<AutoStepState[]>(steps);
  useEffect(() => {
    stepsRef.current = steps;
  }, [steps]);

  const handleFailure = useCallback(
    async (step: AutoStep, error: string) => {
      // Session-expired pattern: don't surface the failure — pause the step in
      // a "running" state, open the re-auth modal, and retry on success. The
      // portal_user step never needs re-auth (it owns the initial SSO).
      if (step !== "portal_user" && looksLikeSessionExpired(error)) {
        stepWaitingForReauthRef.current = step;
        updateStep(step, {
          status: "running",
          description: t(
            "onboarding.autoFlow.stepReauthDesc",
            "Tu sesión con DGI expiró. Reautenticate para que sigamos.",
          ),
          error: undefined,
        });
        setSsoRefreshModalOpen(true);
        return;
      }
      updateStep(step, { status: "failed", error });
      if (orchestrationSessionId) {
        try {
          await reportOnboardingFailure(orchestrationSessionId, step, error);
        } catch {
          /* best effort */
        }
      }
    },
    [orchestrationSessionId, updateStep, t],
  );

  /**
   * Max wall-clock time we'll wait for a single sub-step to reach a terminal
   * status before declaring the session stuck. Generous enough for the
   * slowest step (proveedor_software's Phase A waits on a DGI-mailed
   * confirmation code — up to 15 min in practice) but tight enough that a
   * killed/crashed Python flow doesn't leave the user spinning indefinitely.
   */
  const STEP_STUCK_TIMEOUT_MS = 20 * 60 * 1000;

  const pollSubSession = useCallback(
    (
      subSessionId: string,
      onSuccess: (data: Record<string, unknown>) => void,
      onFail: (error: string) => void,
      /**
       * Optional per-tick observer so callers can surface fine-grained
       * progress (e.g. mapping the Python flow's `currentStep` to a
       * step.description update). Called on every poll cycle, regardless
       * of status, so consumers can drive the UI from running sub-states
       * like `awaiting_mail_confirmation_code`.
       */
      onProgress?: (data: Record<string, unknown>) => void,
    ) => {
      if (pollRef.current) clearInterval(pollRef.current);
      const startedAt = Date.now();
      pollRef.current = setInterval(async () => {
        if (Date.now() - startedAt > STEP_STUCK_TIMEOUT_MS) {
          if (pollRef.current) clearInterval(pollRef.current);
          onFail("Session lost — no response from DGI automation");
          return;
        }
        try {
          const data = await getOnboardingAutoSession(subSessionId);
          if (onProgress) {
            try { onProgress(data); } catch { /* swallow */ }
          }
          const status = String(data.status ?? "");
          if (status === "success") {
            if (pollRef.current) clearInterval(pollRef.current);
            onSuccess(data);
          } else if (status === "failed") {
            if (pollRef.current) clearInterval(pollRef.current);
            onFail(String(data.lastError ?? "Unknown error"));
          }
        } catch {
          /* network error, keep polling */
        }
      }, 3000);
    },
    [],
  );

  useEffect(() => {
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
      if (portalUserPollRef.current) clearInterval(portalUserPollRef.current);
    };
  }, []);

  const chainCaeDownload = useCallback(
    async (sessionId: string, storageStateBase64?: string) => {
      updateStep("cae_download", {
        status: "running",
        description: t(
          "onboarding.autoFlow.stepCaeDownloadNavigating",
          "Navegando DGI y descargando certificados...",
        ),
      });
      try {
        const cae = await continueOnboardingCaeDownload(sessionId, storageStateBase64);
        if (cae.accepted && cae.caeSessionId) {
          pollSubSession(
            cae.caeSessionId,
            async (data) => {
              updateStep("cae_download", { status: "success" });
              // The CAE step always files a fresh solicitud now, so there is
              // never a partially-consumed range to find a resume point for —
              // the last_numbers step it used to chain into is gone. Straight
              // on to idemisor_sync, which reads envíos rather than invoices
              // and still matters: DGI may hold prior envíos for this RUT from
              // another provider.
              if (DEBUG_STEP_BY_STEP) {
                setWaitingForManualContinue("idemisor_sync");
                return;
              }
              await chainIdemisorSync(sessionId);
            },
            async (err) => {
              await handleFailure("cae_download", err);
            },
          );
        } else {
          await handleFailure("cae_download", "Failed to start CAE download");
        }
      } catch (err) {
        await handleFailure(
          "cae_download",
          err instanceof Error ? err.message : "Failed",
        );
      }
    },
    [updateStep, handleFailure, pollSubSession, t, DEBUG_STEP_BY_STEP],
  );

  /**
   * Map the proveedor_software Python flow's currentStep value to a Spanish
   * sub-description. Returns null when the currentStep doesn't warrant a
   * description swap (so the previous description stays put).
   */
  const proveedorSoftwareStepDescription = useCallback(
    (currentStep: unknown): string | null => {
      switch (String(currentStep ?? "")) {
        case "entering_mail_update_menu":
          return t(
            "onboarding.autoFlow.stepProveedorMailMenu",
            "Abriendo el formulario de actualización de email en DGI…",
          );
        case "submitting_mail_update":
          return t(
            "onboarding.autoFlow.stepProveedorMailSubmit",
            "Enviando el nuevo email de contacto a DGI…",
          );
        case "awaiting_mail_confirmation_code":
          return t(
            "onboarding.autoFlow.stepProveedorAwaitingCode",
            "Esperando el código de confirmación que DGI nos envía por email…",
          );
        case "submitting_mail_confirmation_code":
          return t(
            "onboarding.autoFlow.stepProveedorMailConfirm",
            "Confirmando el código en DGI…",
          );
        case "entering_edit_mode":
          return t(
            "onboarding.autoFlow.stepProveedorEditMode",
            "Abriendo la configuración de proveedor de software…",
          );
        case "fiscalo_already_registered":
          return t(
            "onboarding.autoFlow.stepProveedorAlreadyRegistered",
            "Fiscalo ya está registrado — actualizando datos…",
          );
        case "registering_fiscalo":
          return t(
            "onboarding.autoFlow.stepProveedorRegistering",
            "Registrando Fiscalo como tu software de facturación…",
          );
        case "removing_other_software":
          return t(
            "onboarding.autoFlow.stepProveedorRemovingOthers",
            "Quitando otros proveedores anteriores…",
          );
        case "filling_contact_fields":
          return t(
            "onboarding.autoFlow.stepProveedorFillingContact",
            "Actualizando datos de contacto…",
          );
        default:
          return null;
      }
    },
    [t],
  );

  const chainProveedorSoftware = useCallback(
    async (sessionId: string) => {
      updateStep("proveedor_software", {
        status: "running",
        description: t(
          "onboarding.autoFlow.stepProveedorSoftwareStarting",
          "Configurando Fiscalo como tu proveedor de e-Factura…",
        ),
      });
      try {
        const ps = await continueOnboardingProveedorSoftware(sessionId);
        if (ps.accepted && ps.proveedorSoftwareSessionId) {
          pollSubSession(
            ps.proveedorSoftwareSessionId,
            async () => {
              updateStep("proveedor_software", { status: "success" });
            },
            async (err) => {
              await handleFailure("proveedor_software", err);
            },
            (data) => {
              const desc = proveedorSoftwareStepDescription(
                (data as { currentStep?: unknown }).currentStep,
              );
              if (desc) {
                updateStep("proveedor_software", { description: desc });
              }
            },
          );
        } else {
          await handleFailure(
            "proveedor_software",
            "Failed to start proveedor software registration",
          );
        }
      } catch (err) {
        await handleFailure(
          "proveedor_software",
          err instanceof Error ? err.message : "Failed",
        );
      }
    },
    [
      updateStep,
      handleFailure,
      pollSubSession,
      proveedorSoftwareStepDescription,
      t,
    ],
  );

  const chainIdemisorSync = useCallback(
    async (sessionId: string) => {
      updateStep("idemisor_sync", {
        status: "running",
        description: t(
          "onboarding.autoFlow.stepIdemisorSyncStarting",
          "Leyendo IdEmisor desde DGI…",
        ),
      });
      try {
        const isync = await continueOnboardingIdemisorSync(sessionId);
        if (isync.accepted && isync.idemisorSyncSessionId) {
          pollSubSession(
            isync.idemisorSyncSessionId,
            async () => {
              updateStep("idemisor_sync", { status: "success" });
              if (skipProveedorSoftware) return;
              if (DEBUG_STEP_BY_STEP) {
                setWaitingForManualContinue("proveedor_software");
                return;
              }
              await chainProveedorSoftware(sessionId);
            },
            async (err) => {
              await handleFailure("idemisor_sync", err);
            },
          );
        } else {
          await handleFailure(
            "idemisor_sync",
            "Failed to start IdEmisor sync",
          );
        }
      } catch (err) {
        await handleFailure(
          "idemisor_sync",
          err instanceof Error ? err.message : "Failed",
        );
      }
    },
    [
      updateStep,
      handleFailure,
      pollSubSession,
      chainProveedorSoftware,
      skipProveedorSoftware,
      t,
    ],
  );

  const chainCompanyInfo = useCallback(
    async (sessionId: string) => {
      updateStep("company_info", {
        status: "running",
        description: t(
          "onboarding.autoFlow.stepCompanyInfoNavigating",
          "Navegando DGI y descargando datos de la empresa...",
        ),
      });
      try {
        const ci = await continueOnboardingCompanyInfo(sessionId);
        if (ci.accepted && ci.companyInfoSessionId) {
          pollSubSession(
            ci.companyInfoSessionId,
            async (data) => {
              updateStep("company_info", { status: "success" });
              const storageState = (
                data.stepInput as Record<string, unknown> | undefined
              )?.playwrightStorageStateBase64 as string | undefined;
              if (DEBUG_STEP_BY_STEP) {
                lastCompanyInfoDataRef.current = data;
                setWaitingForManualContinue("cae_download");
                return;
              }
              await chainCaeDownload(sessionId, storageState);
            },
            async (err) => {
              await handleFailure("company_info", err);
            },
          );
        } else {
          await handleFailure(
            "company_info",
            "Failed to start company info extraction",
          );
        }
      } catch (err) {
        await handleFailure(
          "company_info",
          err instanceof Error ? err.message : "Failed",
        );
      }
    },
    [updateStep, handleFailure, pollSubSession, chainCaeDownload, t],
  );

  /**
   * Order of steps in the chain. Used by start() to pick a resume point that
   * skips already-completed steps (per the tenant's persisted progress map).
   * For scratch tenants, proveedor_software is dropped from the chain — the
   * user runs the manual DGI homologation after onboarding.
   */
  const STEP_ORDER: readonly AutoStep[] = skipProveedorSoftware
    ? ["portal_user", "company_info", "cae_download"]
    : ["portal_user", "company_info", "cae_download", "proveedor_software"];

  const start = useCallback(async () => {
    if (startedRef.current) return;
    startedRef.current = true;
    setStarted(true);
    startedAtRef.current = Date.now();

    // Resume point — first step the DB doesn't already show as completed.
    // Without this, a mid-flow refresh that lands here would always re-run
    // company_info (the previous hardcoded second step), making finished
    // steps flash through "running → success" again as the backend's
    // idempotency kicks in. Reading `completedSteps` lets us start the
    // chain straight at the actually-pending step.
    const firstIncomplete: AutoStep | null =
      STEP_ORDER.find((s) => !completedSteps?.[s]) ?? null;

    if (firstIncomplete === null) {
      // Everything's already done — nothing to do.
      return;
    }

    const firstStep = firstIncomplete;
    updateStep(firstStep, {
      status: "running",
      description:
        firstStep === "portal_user"
          ? t(
              "onboarding.autoFlow.stepPortalUserPreparing",
              "Preparando credenciales y conectando con DGI...",
            )
          : t(
              "onboarding.autoFlow.stepGenericConnecting",
              "Conectando con DGI...",
            ),
    });

    try {
      const result = await startOnboardingAutoFlow();
      if (!result.accepted) {
        await handleFailure(firstStep, result.message ?? "Failed to start");
        return;
      }
      setOrchestrationSessionId(result.sessionId);

      switch (firstStep) {
        case "portal_user":
          updateStep("portal_user", {
            status: "running",
            description: t(
              "onboarding.autoFlow.stepPortalUserCreating",
              "Creando credenciales e-Factura...",
            ),
          });
          try {
            await createDgiPortalUser();
          } catch {
            /* may already exist */
          }
          updateStep("portal_user", {
            status: "running",
            description: t(
              "onboarding.autoFlow.stepPortalUserAuth",
              "Esperando autenticación con DGI...",
            ),
          });
          setPortalUserModalOpen(true);
          break;
        case "company_info":
          await chainCompanyInfo(result.sessionId);
          break;
        case "cae_download":
          await chainCaeDownload(result.sessionId);
          break;
        case "proveedor_software":
          await chainProveedorSoftware(result.sessionId);
          break;
      }
    } catch (err) {
      await handleFailure(
        firstStep,
        err instanceof Error ? err.message : "Failed to start",
      );
    }
  }, [
    completedSteps,
    updateStep,
    handleFailure,
    chainCompanyInfo,
    chainCaeDownload,
    chainProveedorSoftware,
    t,
  ]);

  // Append to the activity ticker whenever a step finishes or starts running.
  useEffect(() => {
    if (!started) return;
    const live = steps.find((s) => s.status === "running");
    setActivityTicker((prev) => {
      const next = [...prev];
      for (const s of steps) {
        if (
          s.status === "success" &&
          !next.find((e) => e.label === s.label && !e.live)
        ) {
          const time = new Date();
          const ts = time.toLocaleTimeString("es-UY", {
            hour: "2-digit",
            minute: "2-digit",
            second: "2-digit",
          });
          const filtered = next.filter((e) => !(e.live && e.label === s.label));
          filtered.push({ ts, label: s.label });
          return filtered;
        }
      }
      const liveIdx = next.findIndex((e) => e.live);
      if (live) {
        if (liveIdx === -1 || next[liveIdx].label !== live.label) {
          const filtered = next.filter((e) => !e.live);
          filtered.push({
            ts: t("onboarding.autoFlow.now", "Ahora"),
            label: live.label,
            live: true,
          });
          return filtered;
        }
      } else if (liveIdx !== -1) {
        return next.filter((e) => !e.live);
      }
      return next;
    });
  }, [steps, started, t]);

  // Capture completion time once all steps finish.
  useEffect(() => {
    const all = steps.every(
      (s) => s.status === "success" || s.status === "skipped",
    );
    if (all && started && completedAt === null && startedAtRef.current !== null) {
      setCompletedAt(Date.now());
    }
  }, [steps, started, completedAt]);

  // Fire onAllComplete once when everything settles.
  useEffect(() => {
    const all = steps.every(
      (s) => s.status === "success" || s.status === "skipped",
    );
    if (all && started && !completionFiredRef.current) {
      completionFiredRef.current = true;
      void onAllComplete?.();
    }
  }, [steps, started, onAllComplete]);

  // Fire onFailed once if any step terminally fails.
  useEffect(() => {
    const failed = steps.some((s) => s.status === "failed");
    if (failed && !failureFiredRef.current) {
      failureFiredRef.current = true;
      onFailed?.();
    }
  }, [steps, onFailed]);

  /**
   * Map a portal_user step currentStep (emitted by the Python flow) to the
   * Spanish description shown inline. Returns null when the currentStep
   * shouldn't change what's already on screen.
   */
  const portalUserStepDescription = useCallback(
    (currentStep: string | undefined): string | null => {
      switch (currentStep) {
        case "post_push_consent":
        case "dgi_redirect":
          return t(
            "onboarding.autoFlow.stepPortalUserPostPush",
            "Autenticación confirmada. Conectándonos con DGI…",
          );
        case "efactura_solicitud_form":
        case "efactura_resume_session":
        case "efactura_open_form":
          return t(
            "onboarding.autoFlow.stepPortalUserOpeningForm",
            "Abriendo el formulario de Solicitud de Usuario en DGI…",
          );
        case "efactura_regenerating_user":
          return t(
            "onboarding.autoFlow.stepPortalUserRegenerating",
            "Encontramos un usuario antiguo en DGI — lo estamos regenerando…",
          );
        case "efactura_submit":
          return t(
            "onboarding.autoFlow.stepPortalUserSubmitting",
            "Enviando la solicitud a DGI…",
          );
        default:
          return null;
      }
    },
    [t],
  );

  /**
   * Poll the portal_user sub-session for post-SSO progress. The auth modal
   * already closed; we update inline UI based on currentStep, surface the
   * delete-existing-user prompt, and chain to company_info on success.
   */
  const pollPortalUserSession = useCallback(
    (subSessionId: string) => {
      if (portalUserPollRef.current) clearInterval(portalUserPollRef.current);
      const startedAt = Date.now();
      portalUserPollRef.current = setInterval(async () => {
        if (Date.now() - startedAt > STEP_STUCK_TIMEOUT_MS) {
          if (portalUserPollRef.current) clearInterval(portalUserPollRef.current);
          updateStep("portal_user", { awaiting: null });
          await handleFailure(
            "portal_user",
            "Session lost — no response from DGI automation",
          );
          return;
        }
        try {
          const data = await getDgiPortalUserAutomationSession(subSessionId);
          const status = String(data.status ?? "");
          const currentStep = data.currentStep;

          // Step-level UI updates driven by currentStep
          const desc = portalUserStepDescription(currentStep);
          if (desc) updateStep("portal_user", { description: desc });

          if (status === "awaiting_delete_user_confirmation") {
            // Don't re-show the prompt after the user has already responded —
            // there's a 1–2s window where the Python flow still reports the
            // awaiting status while it polls for the input we just sent.
            if (deleteConfirmationSentRef.current) return;
            updateStep("portal_user", {
              awaiting: "delete_existing_user",
              description: t(
                "onboarding.autoFlow.stepPortalUserAwaitingDeleteConfirm",
                "Encontramos un usuario e-Factura existente. Confirmá la regeneración para continuar.",
              ),
            });
            return;
          }

          if (status === "success") {
            if (portalUserPollRef.current) clearInterval(portalUserPollRef.current);
            updateStep("portal_user", { status: "success", awaiting: null });
            if (DEBUG_STEP_BY_STEP) {
              setWaitingForManualContinue("company_info");
              return;
            }
            if (orchestrationSessionId) {
              void chainCompanyInfo(orchestrationSessionId);
            }
          } else if (status === "failed") {
            if (portalUserPollRef.current) clearInterval(portalUserPollRef.current);
            const err = String(data.lastError ?? "Portal user setup failed");
            updateStep("portal_user", { awaiting: null });
            await handleFailure("portal_user", err);
          }
        } catch {
          /* network error, keep polling */
        }
      }, 3000);
    },
    [
      orchestrationSessionId,
      chainCompanyInfo,
      handleFailure,
      portalUserStepDescription,
      updateStep,
      t,
      DEBUG_STEP_BY_STEP,
    ],
  );

  const handlePortalUserSuccess = useCallback(
    async (sessionId?: string) => {
      portalUserSucceededRef.current = true;
      setPortalUserModalOpen(false);
      updateStep("portal_user", {
        status: "running",
        description: t(
          "onboarding.autoFlow.stepPortalUserPostAuth",
          "Conexión con DGI lista. Terminando de configurar tu usuario…",
        ),
      });
      const sid = sessionId ?? portalUserSessionIdRef.current;
      if (sid) {
        portalUserSessionIdRef.current = sid;
        pollPortalUserSession(sid);
      } else {
        // Fallback for legacy callers (no sessionId): assume success after a short delay.
        setTimeout(() => {
          updateStep("portal_user", { status: "success" });
          if (orchestrationSessionId) {
            void chainCompanyInfo(orchestrationSessionId);
          }
        }, 5000);
      }
    },
    [
      orchestrationSessionId,
      updateStep,
      chainCompanyInfo,
      pollPortalUserSession,
      t,
    ],
  );

  const handlePortalUserFailed = useCallback(async () => {
    setPortalUserModalOpen(false);
    if (portalUserPollRef.current) clearInterval(portalUserPollRef.current);
    await handleFailure("portal_user", "Portal user setup failed");
  }, [handleFailure]);

  /**
   * Called whenever the portal-user modal closes (success, user-cancel, or
   * failed). Only triggers the failure path when we have NOT already fired the
   * success branch (i.e. SSO completed and we handed the session off to the
   * inline poller). This stops handlePortalUserFailed from racing the success
   * close and tearing down the still-running Python automation.
   */
  const handlePortalUserModalClosed = useCallback(async () => {
    if (portalUserSucceededRef.current) return; // success path already handled — leave the inline poller alone
    const portalStep = stepsRef.current.find((s) => s.step === "portal_user");
    if (portalStep?.status !== "running") return;
    await handlePortalUserFailed();
  }, [handlePortalUserFailed]);

  const confirmDeleteExistingUser = useCallback(async () => {
    const sid = portalUserSessionIdRef.current;
    if (!sid) return;
    deleteConfirmationSentRef.current = true;
    try {
      await submitDgiPortalUserAutomationStep({
        sessionId: sid,
        confirmDeleteExistingUser: true,
      });
      updateStep("portal_user", {
        awaiting: null,
        description: t(
          "onboarding.autoFlow.stepPortalUserRegenerating",
          "Encontramos un usuario antiguo en DGI — lo estamos regenerando…",
        ),
      });
    } catch (err) {
      deleteConfirmationSentRef.current = false; // allow retry
      await handleFailure(
        "portal_user",
        err instanceof Error ? err.message : "Confirmation failed",
      );
    }
  }, [handleFailure, t, updateStep]);

  const declineDeleteExistingUser = useCallback(async () => {
    const sid = portalUserSessionIdRef.current;
    if (!sid) return;
    deleteConfirmationSentRef.current = true;
    // Drop the awaiting flag synchronously so the confirmation modal closes
    // immediately when the user clicks Cancelar — without this, the modal
    // stays visible until handleFailure flips status to "failed", which
    // races the user's click.
    updateStep("portal_user", { awaiting: null });
    try {
      await submitDgiPortalUserAutomationStep({
        sessionId: sid,
        confirmDeleteExistingUser: false,
      });
    } catch {
      /* best effort */
    }
    if (portalUserPollRef.current) clearInterval(portalUserPollRef.current);
    await handleFailure(
      "portal_user",
      "User declined to delete existing e-Factura account",
    );
  }, [handleFailure, updateStep]);

  const handleSsoRefreshSuccess = useCallback(async () => {
    setSsoRefreshModalOpen(false);
    const step = stepWaitingForReauthRef.current;
    stepWaitingForReauthRef.current = null;
    if (!step || !orchestrationSessionId) return;
    // Storage state was refreshed via the webhook → retry the paused step.
    if (step === "company_info") {
      await chainCompanyInfo(orchestrationSessionId);
    } else if (step === "cae_download") {
      await chainCaeDownload(orchestrationSessionId);
    } else if (step === "proveedor_software") {
      await chainProveedorSoftware(orchestrationSessionId);
    }
  }, [
    orchestrationSessionId,
    chainCompanyInfo,
    chainCaeDownload,
    chainProveedorSoftware,
  ]);

  const handleSsoRefreshFailed = useCallback(async () => {
    setSsoRefreshModalOpen(false);
    const step = stepWaitingForReauthRef.current;
    stepWaitingForReauthRef.current = null;
    if (!step) return;
    // Re-auth itself failed — drop into the normal failed UX so the user can
    // either retry or switch to manual.
    updateStep(step, {
      status: "failed",
      error: "Re-authentication with DGI failed",
    });
    if (orchestrationSessionId) {
      try {
        await reportOnboardingFailure(
          orchestrationSessionId,
          step,
          "Re-authentication with DGI failed",
        );
      } catch {
        /* best effort */
      }
    }
  }, [orchestrationSessionId, updateStep]);

  const manualContinue = useCallback(async () => {
    if (!orchestrationSessionId) return;
    const nextStep = waitingForManualContinue;
    setWaitingForManualContinue(null);
    if (nextStep === "company_info") {
      await chainCompanyInfo(orchestrationSessionId);
    } else if (nextStep === "cae_download") {
      const data = lastCompanyInfoDataRef.current ?? {};
      const stepInput = data.stepInput as Record<string, unknown> | undefined;
      const storageState = stepInput?.playwrightStorageStateBase64 as
        | string
        | undefined;
      await chainCaeDownload(orchestrationSessionId, storageState);
    } else if (nextStep === "proveedor_software") {
      await chainProveedorSoftware(orchestrationSessionId);
    }
  }, [
    orchestrationSessionId,
    waitingForManualContinue,
    chainCompanyInfo,
    chainCaeDownload,
    chainProveedorSoftware,
  ]);

  const retry = useCallback(
    async (step: AutoStep) => {
      if (!orchestrationSessionId) return;
      updateStep(step, { status: "running", error: undefined });
      if (step === "portal_user") {
        try {
          await createDgiPortalUser();
        } catch {
          /* may already exist */
        }
        setPortalUserModalOpen(true);
      } else if (step === "company_info") {
        await chainCompanyInfo(orchestrationSessionId);
      } else if (step === "cae_download") {
        await chainCaeDownload(orchestrationSessionId);
      } else if (step === "proveedor_software") {
        await chainProveedorSoftware(orchestrationSessionId);
      }
    },
    [
      orchestrationSessionId,
      updateStep,
      chainCompanyInfo,
      chainCaeDownload,
      chainProveedorSoftware,
    ],
  );

  const allDone = steps.every(
    (s) => s.status === "success" || s.status === "skipped",
  );
  const hasFailed = steps.some((s) => s.status === "failed");

  const phase: "idle" | "working" | "done" = !started
    ? "idle"
    : allDone
      ? "done"
      : "working";

  const doneCount = steps.filter(
    (s) => s.status === "success" || s.status === "skipped",
  ).length;
  const totalCount = steps.length;

  const tiempoTotalSeconds =
    completedAt !== null && startedAtRef.current !== null
      ? Math.max(1, Math.round((completedAt - startedAtRef.current) / 1000))
      : null;
  const tiempoFmt =
    tiempoTotalSeconds !== null
      ? `${Math.floor(tiempoTotalSeconds / 60)}:${String(
          tiempoTotalSeconds % 60,
        ).padStart(2, "0")}`
      : "--:--";

  return {
    steps,
    activityTicker,
    phase,
    doneCount,
    totalCount,
    tiempoFmt,
    hasFailed,
    waitingForManualContinue,
    portalUserModalOpen,
    setPortalUserModalOpen,
    ssoRefreshModalOpen,
    setSsoRefreshModalOpen,
    start,
    retry,
    manualContinue,
    handlePortalUserSuccess,
    handlePortalUserFailed,
    handlePortalUserModalClosed,
    confirmDeleteExistingUser,
    declineDeleteExistingUser,
    handleSsoRefreshSuccess,
    handleSsoRefreshFailed,
  };
}
